import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Settings2, Loader2, AlertTriangle, Sparkles } from 'lucide-react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { ModelCapabilityBadge } from '@easyflow/ui-ai-routing';
import { NativeToolsPicker } from '@/components/ai/NativeToolsPicker';
import { AgentClaudeSkills } from '@/components/ai/AgentClaudeSkills';
import { CustomLLMPicker } from '@/components/ai/CustomLLMPicker';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';
import { PROVIDER_LIST_FOR_LLM_PICKER, type LLMProvider, type ModelCapabilities } from '@/constants/ai-providers';

const LLM_PROVIDERS = PROVIDER_LIST_FOR_LLM_PICKER;

interface LLMModelEntry {
  id: string;
  name: string;
  desc: string;
  badge?: string;
  badgeColor?: string;
  capabilities?: ModelCapabilities;
}

const PROVIDER_FALLBACK_MODEL_ID: Record<string, string> = {
  openai: 'gpt-5.4-mini',
  claude: 'claude-sonnet-4-6',
  gemini: 'gemini-2.5-flash',
  custom: '',
};

export interface LLMConfig {
  topP?: number;
  topK?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
  effort?: 'auto' | 'low' | 'medium' | 'high';
  thinking?: boolean;
  nativeTools?: string[];
  enableWebSearch?: boolean;
  enableCodeExecution?: boolean;
  enableWebFetch?: boolean;
  claudeServerTools?: string[];
  reasoningEffort?: 'auto' | 'low' | 'medium' | 'high';
  // Task #573 V4: Claude advanced tool-calling flags (per-agent only).
  useToolSearch?: 'auto' | 'always' | 'never';
  useAdvancedToolUse?: boolean;
  useTokenEfficientTools?: boolean;
  disableParallelToolUse?: boolean;
  enableMemoryTool?: boolean;
  eagerInputStreaming?: boolean;
}

interface LLMProviderConfigProps {
  provider: string;
  model: string;
  llmConfig: LLMConfig;
  onProviderChange: (provider: string, defaultModel: string) => void;
  onModelChange: (model: string) => void;
  onConfigChange: (config: LLMConfig) => void;
  savedModel?: string;
  loading?: boolean;
  compact?: boolean;
  fixedProvider?: string;
  hideProviderSelector?: boolean;
  fallbackInfo?: { active: boolean; originalModel?: string; resolvedModel?: string };
  onAcceptFallback?: () => void;
  // Task #1984 round B — riferimenti tenant a endpoint/modello custom.
  customEndpointId?: string | null;
  customModelId?: string | null;
  onCustomChange?: (endpointId: string | null, modelId: string | null, modelName: string) => void;
  // Task #2685 — ID ai_agent per l'assegnazione Claude Skills per-agente.
  // Quando assente (agente non ancora salvato) la sezione Skills mostra un hint.
  agentId?: string | null;
}

export function LLMProviderConfig({ provider, model, llmConfig, onProviderChange, onModelChange, onConfigChange, savedModel, loading, compact, fixedProvider, hideProviderSelector, fallbackInfo, onAcceptFallback, customEndpointId, customModelId, onCustomChange, agentId }: LLMProviderConfigProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const currentProvider = fixedProvider || provider || 'openai';

  // Centralized model fetch via React Query hook (Task #1066)
  const llmModelsQuery = useChatModelOptions(currentProvider as LLMProvider);

  const apiModelsForCurrent = React.useMemo(() => {
    const data = llmModelsQuery.data;
    if (!data?.models?.length) return null;
    const mapped = data.models
      .filter((m) => m.status !== 'deprecated')
      .map((m) => {
        const ctx = m.contextWindow ?? 0;
        const ctxLabel = ctx >= 1000000
          ? `${(ctx / 1000000).toFixed(0)}M contesto`
          : ctx > 0
            ? `${(ctx / 1000).toFixed(0)}K contesto`
            : '';
        const priceLabel = m.pricing && m.pricing.input > 0
          ? `$${(m.pricing.input * 1000).toFixed(2)}/${(m.pricing.output * 1000).toFixed(2)} per 1M tok`
          : '';
        const baseDesc = (m.description || '').substring(0, 60);
        const desc = [baseDesc, priceLabel, ctxLabel].filter(Boolean).join(' | ');
        return {
          id: m.id,
          name: m.displayName || m.name || m.id,
          desc,
          badge: m.isRecommended ? 'Consigliato' : m.status === 'preview' ? 'Preview' : undefined,
          badgeColor: m.isRecommended ? 'bg-green-100 text-green-700' : m.status === 'preview' ? 'bg-amber-100 text-amber-700' : undefined,
          capabilities: m.capabilities,
        };
      });
    return mapped.length > 0 ? mapped : null;
  }, [llmModelsQuery.data]);

  const loadingModels = llmModelsQuery.isLoading || llmModelsQuery.isFetching;
  const _rawSource = llmModelsQuery.data?.source;
  const modelSource: 'live' | 'fallback' | undefined = _rawSource === 'cache' ? undefined : _rawSource;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">Caricamento configurazione...</span>
      </div>
    );
  }

  const isFromApi = !!apiModelsForCurrent;
  const isOffline = modelSource === 'fallback';
  const baseModels: LLMModelEntry[] = apiModelsForCurrent ?? [];

  const effectiveSavedModel = savedModel || model;
  const savedModelBelongsToProvider = effectiveSavedModel
    ? inferProviderFromModelFrontend(effectiveSavedModel) === currentProvider
    : false;
  let models = baseModels;
  if (effectiveSavedModel && savedModelBelongsToProvider && !baseModels.some(m => m.id === effectiveSavedModel)) {
    const recommended = baseModels.find(m => m.badge === 'Consigliato');
    const recommendedHint = recommended ? ` Consigliato: ${recommended.name}` : '';
    models = [
      {
        id: effectiveSavedModel,
        name: effectiveSavedModel,
        desc: `Modello precedentemente configurato — non più disponibile.${recommendedHint}`,
        badge: 'Non disponibile',
        badgeColor: 'bg-red-100 text-red-700',
      },
      ...baseModels,
    ];
  }

  const providerSpecificFields = () => {
    const fields: React.ReactNode[] = [];

    if (currentProvider === 'openai') {
      const modelLower = (model || '').toLowerCase();
      const isReasoningModel = modelLower.startsWith('o1') || modelLower.startsWith('o3') || modelLower.startsWith('o4');

      fields.push(
        <div key="reasoningEffort" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Reasoning Effort</Label>
            <span className="text-[10px] text-gray-400">{isReasoningModel ? 'Profondità del ragionamento' : 'Attivo solo su modelli o-series (o1/o3/o4)'}</span>
          </div>
          <div className="flex gap-2">
            {(['auto', 'low', 'medium', 'high'] as const).map(level => (
              <button
                key={level}
                type="button"
                onClick={() => onConfigChange({ ...llmConfig, reasoningEffort: level })}
                disabled={!isReasoningModel}
                className={`flex-1 px-2 py-1 rounded text-xs font-medium border transition-all ${
                  (llmConfig.reasoningEffort || 'auto') === level
                    ? 'bg-sky-100 text-sky-700 border-sky-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                } ${!isReasoningModel ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {level === 'auto' ? 'Auto' : level === 'low' ? 'Basso' : level === 'medium' ? 'Medio' : 'Alto'}
              </button>
            ))}
          </div>
        </div>,
        <div key="freq" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Frequency Penalty: {llmConfig.frequencyPenalty ?? 0}</Label>
            <span className="text-[10px] text-gray-400">Riduce ripetizioni</span>
          </div>
          <input
            type="range"
            value={llmConfig.frequencyPenalty ?? 0}
            onChange={(e) => onConfigChange({ ...llmConfig, frequencyPenalty: parseFloat(e.target.value) })}
            min={0} max={2} step={0.1}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-500"
          />
        </div>,
        <div key="pres" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Presence Penalty: {llmConfig.presencePenalty ?? 0}</Label>
            <span className="text-[10px] text-gray-400">Favorisce nuovi topic</span>
          </div>
          <input
            type="range"
            value={llmConfig.presencePenalty ?? 0}
            onChange={(e) => onConfigChange({ ...llmConfig, presencePenalty: parseFloat(e.target.value) })}
            min={0} max={2} step={0.1}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-500"
          />
        </div>,
        <div key="openaiNativeTools">
          <NativeToolsPicker
            provider={currentProvider}
            model={model}
            selected={llmConfig.nativeTools || []}
            onChange={(next) =>
              onConfigChange({ ...llmConfig, nativeTools: next.length > 0 ? next : undefined })
            }
            label="Tool Nativi OpenAI"
            description="Tool eseguiti server-side via Responses API. Attivandone almeno uno la chiamata passa automaticamente da Chat Completions a Responses API."
          />
        </div>
      );
    }

    if (currentProvider === 'claude') {
      fields.push(
        <div key="thinking" className="flex items-center justify-between py-1">
          <div>
            <Label className="text-xs">Thinking Adattivo</Label>
            <p className="text-[10px] text-gray-400 mt-0.5">Il modello mostra il ragionamento interno (solo streaming)</p>
          </div>
          <Switch
            checked={llmConfig.thinking ?? false}
            onCheckedChange={(checked) => onConfigChange({ ...llmConfig, thinking: checked })}
          />
        </div>,
        ...(llmConfig.thinking ? [
          <div key="thinkingNotice" className="flex items-start gap-2 p-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="text-[10px] leading-tight text-amber-800 dark:text-amber-200 space-y-1">
              <p><strong>Vincoli con thinking attivo:</strong></p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>La <strong>temperatura</strong> viene forzata a <code>1</code> (lo slider sopra è ignorato — requisito Anthropic).</li>
                <li><code>top_p</code> / <code>top_k</code> non vengono inviati.</li>
                <li>Il <strong>budget di ragionamento</strong> consuma <code>max_tokens</code>: alza <code>max_tokens</code> ad almeno 8.192 — meglio 16.384 — per evitare risposte tronche{/opus/i.test(model || '') ? ', soprattutto su Opus 4.6/4.7' : ''}.</li>
              </ul>
            </div>
          </div>
        ] : []),
        <div key="effort" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Effort</Label>
            <span className="text-[10px] text-gray-400">Livello di impegno del modello</span>
          </div>
          <div className="flex gap-2">
            {(['auto', 'low', 'medium', 'high'] as const).map(level => (
              <button
                key={level}
                type="button"
                onClick={() => onConfigChange({ ...llmConfig, effort: level })}
                className={`flex-1 px-2 py-1 rounded text-xs font-medium border transition-all ${
                  (llmConfig.effort || 'auto') === level
                    ? 'bg-purple-100 text-purple-700 border-purple-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {level === 'auto' ? 'Auto' : level === 'low' ? 'Basso' : level === 'medium' ? 'Medio' : 'Alto'}
              </button>
            ))}
          </div>
        </div>,
        <div key="serverTools" className="space-y-2">
          <Label className="text-xs">Server Tools Claude</Label>
          <p className="text-[10px] text-gray-400 -mt-1">Tool eseguiti lato server da Anthropic</p>
          <div className="grid grid-cols-1 gap-1.5">
            {[
              { id: 'web_search', name: 'Web Search', desc: 'Ricerca web in tempo reale con citazioni' },
              { id: 'code_execution', name: 'Code Execution', desc: 'Esecuzione codice Python in sandbox sicura' },
              { id: 'web_fetch', name: 'Web Fetch', desc: 'Scarica e legge contenuti da URL' },
            ].map(tool => (
              <label key={tool.id} className="flex items-start gap-1.5 text-xs cursor-pointer p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                <input
                  type="checkbox"
                  checked={(llmConfig.claudeServerTools || []).includes(tool.id)}
                  onChange={(e) => {
                    const current = llmConfig.claudeServerTools || [];
                    const updated = e.target.checked ? [...current, tool.id] : current.filter(t => t !== tool.id);
                    onConfigChange({ ...llmConfig, claudeServerTools: updated });
                  }}
                  className="rounded border-gray-300 mt-0.5"
                />
                <div>
                  <span className="font-medium">{tool.name}</span>
                  <p className="text-[10px] text-muted-foreground leading-tight">{tool.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>,
        <div key="claudeAdvanced" className="space-y-2 pt-1 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Funzionalità Avanzate (Beta 2026)</Label>
            <span className="text-[10px] text-gray-400">Opt-in per agente</span>
          </div>
          <div className="flex items-start justify-between gap-2 p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium">Tool Search dinamico</span>
              <p className="text-[10px] text-muted-foreground leading-tight">Carica gli schemi tool on-demand. <em>auto</em> attiva sopra la soglia (~50 tool).</p>
            </div>
            <select
              className="h-7 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs px-1.5"
              value={llmConfig.useToolSearch ?? 'auto'}
              onChange={(e) => onConfigChange({ ...llmConfig, useToolSearch: e.target.value as LLMConfig['useToolSearch'] })}
            >
              <option value="auto">auto</option>
              <option value="always">always</option>
              <option value="never">never</option>
            </select>
          </div>
          {([
            { key: 'useAdvancedToolUse', label: 'Programmatic Tool Calling', desc: 'Codice Python che orchestra le chiamate tool — meno round-trip su workflow complessi. Richiede Sonnet 4.5+/Opus 4.7.' },
            { key: 'useTokenEfficientTools', label: 'Token-Efficient Tools', desc: 'Riduce i token consumati dalle tool definition (~14%).' },
            { key: 'eagerInputStreaming', label: 'Fine-grained Tool Streaming', desc: 'Streaming incrementale degli argomenti dei tool. Utile su output lunghi.' },
            { key: 'disableParallelToolUse', label: 'Disabilita parallel tool use', desc: 'Forza esecuzione sequenziale. Consigliato per voce e dipendenze step-by-step.' },
            { key: 'enableMemoryTool', label: 'Memory Tool', desc: 'Tool stateful: Claude persiste informazioni tra sessioni. Richiede Sonnet 4.5+/Opus 4.7.' },
          ] as Array<{ key: keyof LLMConfig; label: string; desc: string }>).map(opt => (
            <label key={opt.key as string} className="flex items-start justify-between gap-2 p-1.5 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium">{opt.label}</span>
                <p className="text-[10px] text-muted-foreground leading-tight">{opt.desc}</p>
              </div>
              <Switch
                checked={!!llmConfig[opt.key]}
                onCheckedChange={(checked) => onConfigChange({ ...llmConfig, [opt.key]: checked })}
              />
            </label>
          ))}
        </div>,
        agentId ? (
          <AgentClaudeSkills key="claudeSkills" agentId={agentId} />
        ) : (
          <div key="claudeSkillsHint" className="space-y-1 pt-1 border-t border-gray-200 dark:border-gray-700">
            <Label className="text-xs flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-purple-500" />
              Claude Skills
            </Label>
            <p className="text-[10px] text-muted-foreground italic">
              Salva prima l'agente per assegnare le Claude Skills.
            </p>
          </div>
        ),
      );
    }

    if (currentProvider === 'gemini') {
      fields.push(
        <div key="geminiReasoning" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Livello di ragionamento</Label>
            <span className="text-[10px] text-gray-400">Profondità del thinking Gemini</span>
          </div>
          <div className="flex gap-2">
            {(['auto', 'low', 'medium', 'high'] as const).map(level => (
              <button
                key={level}
                type="button"
                onClick={() => onConfigChange({ ...llmConfig, effort: level })}
                className={`flex-1 px-2 py-1 rounded text-xs font-medium border transition-all ${
                  (llmConfig.effort || 'auto') === level
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {level === 'auto' ? 'Auto' : level === 'low' ? 'Basso' : level === 'medium' ? 'Medio' : 'Alto'}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400">
            Gemini 3.x: mappato su <code>thinkingLevel</code> (LOW/MEDIUM/HIGH). Gemini 2.5+: mappato sul legacy <code>thinkingBudget</code>. Auto lascia decidere al modello (Flash-Lite usa MINIMAL).
          </p>
        </div>,
        <div key="topP" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Top P: {llmConfig.topP ?? 0.95}</Label>
            <span className="text-[10px] text-gray-400">Nucleus sampling</span>
          </div>
          <input
            type="range"
            value={llmConfig.topP ?? 0.95}
            onChange={(e) => onConfigChange({ ...llmConfig, topP: parseFloat(e.target.value) })}
            min={0} max={1} step={0.05}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
        </div>,
        <div key="topK" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Top K: {llmConfig.topK ?? 40}</Label>
            <span className="text-[10px] text-gray-400">Numero di token candidati</span>
          </div>
          <input
            type="range"
            value={llmConfig.topK ?? 40}
            onChange={(e) => onConfigChange({ ...llmConfig, topK: parseInt(e.target.value) })}
            min={1} max={100} step={1}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
        </div>,
        <div key="freq-gemini" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Frequency Penalty: {llmConfig.frequencyPenalty ?? 0}</Label>
            <span className="text-[10px] text-gray-400">Riduce ripetizioni</span>
          </div>
          <input
            type="range"
            value={llmConfig.frequencyPenalty ?? 0}
            onChange={(e) => onConfigChange({ ...llmConfig, frequencyPenalty: parseFloat(e.target.value) })}
            min={0} max={2} step={0.1}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
        </div>,
        <div key="pres-gemini" className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Presence Penalty: {llmConfig.presencePenalty ?? 0}</Label>
            <span className="text-[10px] text-gray-400">Favorisce nuovi topic</span>
          </div>
          <input
            type="range"
            value={llmConfig.presencePenalty ?? 0}
            onChange={(e) => onConfigChange({ ...llmConfig, presencePenalty: parseFloat(e.target.value) })}
            min={0} max={2} step={0.1}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
        </div>,
        <div key="nativeTools" className="space-y-2">
          <Label className="text-xs">Tool Nativi Gemini</Label>
          {(() => {
            const hasMaps = llmConfig.nativeTools?.includes('google_maps') ?? false;
            const nonMapsTools = ['google_search', 'code_execution', 'url_context'];

            const handleToggle = (tool: string, checked: boolean) => {
              if (tool === 'google_maps') {
                if (checked) {
                  onConfigChange({ ...llmConfig, nativeTools: ['google_maps'] });
                } else {
                  onConfigChange({ ...llmConfig, nativeTools: [] });
                }
              } else {
                const current = (llmConfig.nativeTools || []).filter(t => t !== 'google_maps');
                const updated = checked
                  ? [...current, tool]
                  : current.filter(t => t !== tool);
                onConfigChange({ ...llmConfig, nativeTools: updated });
              }
            };

            return (
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'google_search', icon: '🔍', name: 'Google Search', desc: 'Ricerca web in tempo reale. Chat + Voice.' },
                  { id: 'code_execution', icon: '💻', name: 'Code Execution', desc: 'Esecuzione codice Python in sandbox. Solo chat.' },
                  { id: 'url_context', icon: '🔗', name: 'URL Context', desc: 'Legge contenuti da URL nel prompt. Solo chat.' },
                ].map(tool => (
                  <label key={tool.id} className={`flex items-start gap-1.5 text-xs cursor-pointer p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 ${hasMaps ? 'opacity-40' : ''}`}
                    title={hasMaps ? 'Attivando questo tool, Google Maps verrà disattivato (non compatibile).' : ''}>
                    <input
                      type="checkbox"
                      checked={(llmConfig.nativeTools || []).includes(tool.id)}
                      onChange={(e) => handleToggle(tool.id, e.target.checked)}
                      className="rounded border-gray-300 mt-0.5"
                    />
                    <div>
                      <span className="font-medium">{tool.icon} {tool.name}</span>
                      <p className="text-[10px] text-muted-foreground leading-tight">{tool.desc}</p>
                    </div>
                  </label>
                ))}
                <label className={`flex items-start gap-1.5 text-xs cursor-pointer p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 ${!hasMaps && (llmConfig.nativeTools || []).length > 0 ? 'opacity-40' : ''}`}
                  title={!hasMaps && (llmConfig.nativeTools || []).length > 0 ? 'Attivando Maps, gli altri tool nativi verranno disattivati (non compatibile).' : ''}>
                  <input
                    type="checkbox"
                    checked={hasMaps}
                    onChange={(e) => handleToggle('google_maps', e.target.checked)}
                    className="rounded border-gray-300 mt-0.5"
                  />
                  <div>
                    <span className="font-medium">{"🗺️"} Google Maps</span>
                    <p className="text-[10px] text-muted-foreground leading-tight">Dati geolocalizzati Google Maps. Solo chat.</p>
                  </div>
                </label>
              </div>
            );
          })()}
          <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
            ⚠️ Modelli 2.x: i tool nativi non funzionano se l'agente ha tool custom.
            Modelli 3.x (es. Gemini 3 Flash): tool nativi e custom funzionano insieme.
          </p>
          <p className="text-[10px] text-amber-600 dark:text-amber-400 leading-tight">
            🗺️ Google Maps non può essere combinato con altri tool nativi (limitazione API Google). Se attivi Maps, gli altri vengono disattivati e viceversa.
          </p>
        </div>
      );
    }

    return fields;
  };

  const advancedFields = providerSpecificFields();

  const modelIsUnavailable = effectiveSavedModel && savedModelBelongsToProvider && models.some(m => m.id === effectiveSavedModel && m.badge === 'Non disponibile');
  const recommendedModel = baseModels.find(m => m.badge === 'Consigliato');

  return (
    <div className="space-y-3">
      {fallbackInfo?.active && fallbackInfo.originalModel && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Attenzione: il modello <code className="bg-amber-100 px-1 rounded">{fallbackInfo.originalModel}</code> non è più disponibile.</p>
            <p className="mt-1">Il sistema sta usando <code className="bg-amber-100 px-1 rounded">{fallbackInfo.resolvedModel || model}</code> come fallback. Aggiornare la configurazione.</p>
            {onAcceptFallback && (
              <button
                type="button"
                onClick={onAcceptFallback}
                className="mt-2 px-3 py-1 rounded bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors"
              >
                Accetta {fallbackInfo.resolvedModel || model} come nuovo modello
              </button>
            )}
          </div>
        </div>
      )}

      {modelIsUnavailable && !fallbackInfo?.active && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg border border-red-300 bg-red-50 text-red-800 text-xs">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Il modello <code className="bg-red-100 px-1 rounded">{effectiveSavedModel}</code> non è più nella lista dei modelli disponibili.</p>
            <p className="mt-0.5">
              {recommendedModel
                ? <>Si consiglia di passare a <strong>{recommendedModel.name}</strong> prima di salvare.</>
                : <>Si consiglia di selezionare un altro modello prima di salvare.</>
              }
            </p>
          </div>
        </div>
      )}

      {!hideProviderSelector && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Provider LLM</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {LLM_PROVIDERS
              .map(p => {
                const isSelected = currentProvider === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      if (fixedProvider) return;
                      const defaultModel = PROVIDER_FALLBACK_MODEL_ID[p.id] || 'gpt-5.4-mini';
                      onProviderChange(p.id, defaultModel);
                    }}
                    disabled={!!fixedProvider}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                      isSelected
                        ? `${p.color} border-current ring-1 ring-current/20`
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                    } ${fixedProvider ? 'cursor-default' : ''}`}
                  >
                    <span className="text-sm">{p.icon}</span>
                    <span className="truncate">{p.name}</span>
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {currentProvider === 'custom' ? (
        // Task #1984 round B — picker dedicato per endpoint custom OpenAI-compatible.
        <div className="space-y-2">
          <Label className="text-sm font-medium">Endpoint & Modello custom</Label>
          <CustomLLMPicker
            endpointId={customEndpointId ?? null}
            modelId={customModelId ?? null}
            modelName={model}
            onChange={(epId, mdId, mdName) => {
              if (onCustomChange) onCustomChange(epId, mdId, mdName);
              if (mdName) onModelChange(mdName);
            }}
          />
        </div>
      ) : (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Modello AI</Label>
          <div className="flex items-center gap-1.5">
            {isOffline && !loadingModels && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-300" title="La lista modelli proviene dal fallback locale: la chiamata API al provider non è andata a buon fine. Verifica che la chiave API sia configurata e valida.">
                ⚠️ Lista offline — verifica chiave API
              </Badge>
            )}
            {!isOffline && isFromApi && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-emerald-50 text-emerald-600 border-emerald-200">Live</Badge>
            )}
            {loadingModels && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </div>
        </div>
        <Select value={model} onValueChange={onModelChange}>
          <SelectTrigger className={compact ? "h-8 text-xs" : "h-9"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {models.map(m => (
              <SelectItem key={m.id} value={m.id}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{m.name}</span>
                  <span className="text-gray-400 text-xs">{m.desc}</span>
                  {m.badge && (
                    <Badge variant="outline" className={`text-[10px] px-1 py-0 ${m.badgeColor}`}>
                      {m.badge}
                    </Badge>
                  )}
                  {m.capabilities && (
                    <ModelCapabilityBadge capabilities={m.capabilities} />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      )}

      {advancedFields.length > 0 && !compact && currentProvider !== 'custom' && (
        <div className="border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-xs font-medium text-gray-600"
          >
            <span className="flex items-center gap-1.5">
              <Settings2 className="w-3.5 h-3.5" />
              Configurazione Avanzata Provider
            </span>
            {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {showAdvanced && (
            <div className="p-3 space-y-3 bg-white">
              {advancedFields}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function inferProviderFromModelFrontend(model: string): string {
  if (!model) return 'openai';
  const m = model.toLowerCase();
  if (m.startsWith('gemini-') || m.startsWith('gemini2') || m.startsWith('gemini3')) return 'gemini';
  if (m.startsWith('claude-') || m.startsWith('claude ')) return 'claude';
  if (m.startsWith('gpt-') || m.startsWith('o1') || m.startsWith('o3') || m.startsWith('o4') || m.startsWith('chatgpt')) return 'openai';
  return 'openai';
}

export { LLM_PROVIDERS };
export default LLMProviderConfig;
